import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
    TextField,
    MenuItem,
    Grid,
    FormControlLabel,
    Switch,
    Button,
    InputAdornment,
    Typography,
    Box,
    CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SaveIcon from '@material-ui/icons/Save';
import Autocomplete from '@material-ui/lab/Autocomplete';
import axios from 'axios';
import { toastr } from 'react-redux-toastr';

import { URL } from '../../assets/js/constants';
import { toggleShowDialog } from '../../store/actions/dialog';
import { getCategoriasAtivas } from '../../store/actions/categoria';
import { Number as NumberFormat } from '../../components/form/NumberFormat';
import { unidadesDeMedida } from '../../assets/js/constants';
import normalizarTitulo from '../../assets/js/normalizarTitulo';
import TituloPagina from '../../components/layout/TituloPagina';
import If from '../../components/condicional/if';
import LoadingOverlay from '../../components/layout/LoadingOverlay';
import {
    addProduto,
    editProduto,
    uploadImagemProduto,
    getCorProduto,
    getEstampaProduto,
    getTamanhoProduto,
    getProduto,
    deletarVariacaoDoProduto
} from '../../store/actions/produto';

const useStyles = makeStyles((theme) => ({
    buttonProgress: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12,
    },
}));

const VARIACAO_INICIAL = { id: '', codigoTamanho: 1, codigoCor: 1, codigoEstampa: 1, estoque: 0 };

const ProdutoCadastro = props => {
    const history = useHistory();
    const location = useLocation();
    const classes = useStyles();
    const {
        categorias,
        getProduto,
        getCategoriasAtivas,
        getCorProduto,
        getTamanhoProduto,
        getEstampaProduto,
        addProduto,
        editProduto,
        produto,
        tamanhoProduto,
        corProduto,
        estampaProduto,
        negocioDoUsuario,
        deletarVariacaoDoProduto,
        loading,
        processing,
        produtoSalvo
    } = props;
    const [disableFields, setDisableFields] = useState(false);
    const [editando, setEditando] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingMessage, setProcessingMessage] = useState('');

    useEffect(() => {
        getCategoriasAtivas(negocioDoUsuario);
        getCorProduto();
        getTamanhoProduto();
        getEstampaProduto();
    }, [
        getCategoriasAtivas,
        getCorProduto,
        getTamanhoProduto,
        getEstampaProduto,
        negocioDoUsuario
    ]);

    useEffect(() => {
        if (produtoSalvo) setDisableFields(true);
    }, [produtoSalvo]);

    useEffect(() => {
        const id = Number(location?.state?.produtoId);
        const isEditRoute = location.pathname === '/produtos/editar-produto';

        if (isEditRoute && !id) {
            history.replace('/produtos');
            return;
        }

        if (id > 0) {
            getProduto(id);
            setEditando(true);
        }
    }, [getProduto, history, location]);

    const validationSchema = yup.object({
        nome: yup.string()
            .required('Informe o título do anúncio, que pode ser o nome do produto.')
            .min(5, 'O nome deve possuir entre 5 e 100 caracteres.')
            .max(100, 'O nome deve possuir entre 5 e 100 caracteres.'),
        descricao: yup.string()
            .required('Uma descrição bem elaborada do produto é importante para esclarecer o máximo de dúvidas do cliente.')
            .max(1000, 'A descrição deve possuir até 1.000 caracteres.'),
        preco: yup.number()
            .typeError('Informe um valor válido.')
            .required('Informe o preço do produto em moeda corrente.')
            .positive('Informe um valor maior que zero.'),

        // As transportadoras costumam exigir peso mínimo (ex: 0.1kg ou 100g)
        peso: yup.number()
            .typeError('Informe um valor numérico (ex: 0,5 para 500g).')
            .required('O peso é obrigatório para o cálculo do frete.')
            .min(0.01, 'O peso mínimo é 0,01kg (10g).')
            .max(30, 'O peso máximo permitido por etiqueta costuma ser 30kg.'),

        // Limites padrão das transportadoras (Soma das dimensões)
        altura: yup.number()
            .typeError('Informe um valor válido')
            .required('Campo obrigatório.')
            .min(1, 'A altura mínima é 1cm.')
            .max(100, 'A altura máxima permitida é 100cm.'),

        largura: yup.number()
            .typeError('Informe um valor válido')
            .required('Campo obrigatório.')
            .min(10, 'A largura mínima aceita costuma ser 10cm.')
            .max(100, 'A largura máxima permitida é 100cm.'),

        comprimento: yup.number()
            .typeError('Informe um valor válido')
            .required('Campo obrigatório.')
            .min(15, 'O comprimento mínimo aceito costuma ser 15cm.')
            .max(100, 'O comprimento máximo permitido é 100cm.'),

        codigoUnidadeMedida: yup.number()
            .typeError('Informe um valor válido')
            .required('Informe a forma como o produto é vendido.')
            .positive('Informe um valor maior que zero.'),
        idCategoria: yup.number()
            .required('Campo obrigatório.')
            .positive('Campo obrigatório.'),
        quantidadeMinimaAtacado: yup.number()
            .transform((value, originalValue) => originalValue === "" ? undefined : value)
            .when('precoAtacado', {
                // A função recebe o valor atual de 'precoAtacado'
                is: (val) => val > 0,
                then: (schema) => schema
                    .typeError('Informe um número válido')
                    .required('Informe a quantidade mínima para atacado.')
                    .positive('A quantidade deve ser maior que zero')
                    .integer('A quantidade deve ser um número inteiro'),
                otherwise: (schema) => schema
                    .transform((value, originalValue) => originalValue === "" ? undefined : value)
                    .notRequired()
                    .nullable(),
            }),
        // Definimos o schema para as variações
        variacoes: yup.array().of(
            yup.object().shape({
                codigoTamanho: yup.number().required('Obrigatório'),
                codigoCor: yup.number().required('Obrigatório'),
                codigoEstampa: yup.number().required('Obrigatório'),
                estoque: yup.number()
                    .typeError('Informe um número')
                    .min(0, 'Mínimo 0')
                    .required('Obrigatório')
            })
        ).test('logica-variacao', 'Erro na variação', function (value) {
            if (!value || value.length === 0) return true;

            const erros = [];

            value.forEach((item, index) => {
                const t = Number(item.codigoTamanho);
                const c = Number(item.codigoCor);
                const e = Number(item.codigoEstampa);
                const estoque = Number(item.estoque);

                // 1. Regra de Consistência: Se um campo é preenchido, todos devem ser (diferentes de 1)
                const temValorReal = (t !== 1 || c !== 1 || e !== 1);

                if (temValorReal) {
                    if (t === 1 || c === 1 || e === 1) {
                        erros.push(`Variação #${index + 1}: Informe Tamanho, Cor e Estampa (ou deixe todos como "Não se aplica").`);
                    }

                    // 2. Nova Regra: Se tem variação real, estoque deve ser > 0
                    if (estoque <= 0) {
                        erros.push(`Variação #${index + 1}: O estoque deve ser maior que zero.`);
                    }
                }

                // 3. Regra Global: Múltiplas variações não podem ter "Não se aplica"
                if (value.length > 1 && (t === 1 || c === 1 || e === 1)) {
                    erros.push(`Variação #${index + 1}: Remova o "Não se aplica" para cadastrar múltiplas variações.`);
                }
            });

            if (erros.length > 0) {
                // Criamos o erro no nível do array para o Formik capturar em formik.errors.variacoes
                return this.createError({ message: erros[0] });
            }

            return true;
        }).test('unique-combination', 'Existem variações duplicadas (mesma Cor, Tamanho e Estampa). Verifique.', function (list) {
            if (!list || list.length <= 1) return true;

            // Criamos um conjunto de "chaves" únicas
            const combinacoes = list.map(v => `${v.codigoCor}-${v.codigoTamanho}-${v.codigoEstampa}`);

            // Comparamos o tamanho do Set (que remove duplicatas) com o tamanho da lista original
            const temDuplicados = new Set(combinacoes).size !== combinacoes.length;

            return !temDuplicados; // Se houver duplicados, retorna false e exibe a mensagem acima
        })
    });


    const valoresIniciaisEstaveis = React.useMemo(() => {
        return {
            id: produto.id ?? '',
            nome: produto.nome ?? '',
            descricao: produto.descricao ?? '',
            preco: produto.preco ?? '',
            precoPromocional: produto.precoPromocional ?? '',
            precoAtacado: produto.precoAtacado ?? '',
            quantidadeMinimaAtacado: produto.quantidadeMinimaAtacado ?? '',
            estoque: produto.estoque ?? '',
            codigoUnidadeMedida: produto.codigoUnidadeMedida ?? '',
            idCategoria: produto.idCategoria ?? '',
            ativo: produto.ativo ?? false,
            peso: produto.peso ?? '',
            altura: produto.altura ?? '',
            largura: produto.largura ?? '',
            comprimento: produto.comprimento ?? '',
            aplicarDescontoPix: produto.aplicarDescontoPix ?? false,
            // Aqui usamos a constante externa para evitar novas referências inúteis
            variacoes: (produto.variacoes && produto.variacoes.length > 0)
                ? produto.variacoes
                : [{ ...VARIACAO_INICIAL }],
        };
    }, [produto.id]);

    const formik = useFormik({
        initialValues: valoresIniciaisEstaveis,
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {

            if (values.precoAtacado > 0 && !values.quantidadeMinimaAtacado) {
                return toastr.error('Erro', 'Informe quantidade mínima de produtos para acionar o preço de atacado.');
            }

            setProcessingMessage('Salvando...');

            const newValues = { ...values, nome: normalizarTitulo(values.nome), idVendedor: negocioDoUsuario }

            if (produto.id > 0) {
                editProduto(newValues);
            } else {
                addProduto(newValues);
            }

            setProcessingMessage('');
        },
    });

    const handleAddVariacao = () => {
        const novasVariacoes = [...formik.values.variacoes, { ...VARIACAO_INICIAL }];
        formik.setFieldValue('variacoes', novasVariacoes);
    };

    const handleChangeVariacao = (e, ix) => {
        const { name, value } = e.target;
        const novasVariacoes = [...formik.values.variacoes];
        novasVariacoes[ix][name] = value;

        formik.setFieldValue('variacoes', novasVariacoes, true);
    };

    const handleDeleteVariacao = async (id, ix) => {
        try {
            setProcessingMessage('Processando...');
            setIsProcessing(true);

            if (id > 0) {
                axios.delete(`${URL}/produtos/variacao/${id}`)
                    .then(_ => {
                        const novasVariacoes = formik.values.variacoes.filter((_, index) => index !== ix);
                        formik.setFieldValue('variacoes', novasVariacoes);
                        toastr.success('Sucesso', 'Variação excluída.')
                    })
                    .catch(e => {
                        if (e.response && e.response.data) {
                            const { msg } = e.response.data;
                            toastr.error('Erro', msg);
                        } else {
                            toastr.error('Erro', 'Servidor fora do ar');
                        }
                    });
            } else {
                const novasVariacoes = formik.values.variacoes.filter((_, index) => index !== ix);
                formik.setFieldValue('variacoes', novasVariacoes);
                toastr.success('Sucesso', 'Variação excluída.')
            }

        } catch (e) {
            console.error("Erro inesperado na exclusão", e);
        } finally {
            setIsProcessing(false);
            setProcessingMessage('');
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, height: '70vh' }}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    return (
        <>
            <TituloPagina titulo='Cadastro de Produtos' subtitulo={new Date().toLocaleString('pt-BR')} />
            <Grid item xs={12} style={{ paddingTop: '15px' }}>
                <Grid>
                    <Typography variant='body1' component='p'>
                        Informe abaixo os dados e características do produto. Seja convincente na descrição.
                    </Typography>
                </Grid>
                <form onSubmit={formik.handleSubmit} noValidate>
                    <div style={{ flexGrow: 1, paddingTop: '15px' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    id="nome"
                                    name="nome"
                                    label="Nome do Produto"
                                    variant="filled"
                                    disabled={disableFields}
                                    value={formik.values.nome}
                                    onChange={formik.handleChange}
                                    error={formik.touched.nome && Boolean(formik.errors.nome)}
                                    helperText={formik.touched.nome && formik.errors.nome} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    id="descricao"
                                    name="descricao"
                                    label="Descrição"
                                    variant="filled"
                                    multiline
                                    rows={10}
                                    disabled={disableFields}
                                    value={formik.values.descricao}
                                    onChange={formik.handleChange}
                                    error={formik.touched.descricao && Boolean(formik.errors.descricao)}
                                    helperText={formik.touched.descricao && formik.errors.descricao}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    fullWidth
                                    id="preco"
                                    name="preco"
                                    label="Preço do Produto"
                                    variant="filled"
                                    disabled={disableFields}
                                    value={formik.values.preco}
                                    defaultValue={0}
                                    onChange={formik.handleChange}
                                    error={formik.touched.preco && Boolean(formik.errors.preco)}
                                    helperText={formik.touched.preco && formik.errors.preco}
                                    InputProps={{
                                        inputComponent: NumberFormat,
                                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                    }} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    fullWidth
                                    id="precoPromocional"
                                    name="precoPromocional"
                                    label="Preço Promocional"
                                    variant="filled"
                                    disabled={disableFields}
                                    defaultValue={0}
                                    value={formik.values.precoPromocional}
                                    onChange={formik.handleChange}
                                    error={formik.touched.precoPromocional && Boolean(formik.errors.precoPromocional)}
                                    helperText={formik.touched.precoPromocional && formik.errors.precoPromocional}
                                    InputProps={{
                                        inputComponent: NumberFormat,
                                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                    }} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    fullWidth
                                    id="precoAtacado"
                                    name="precoAtacado"
                                    label="Preço Atacado"
                                    variant="filled"
                                    disabled={disableFields}
                                    defaultValue={0}
                                    value={formik.values.precoAtacado}
                                    onChange={formik.handleChange}
                                    error={formik.touched.precoAtacado && Boolean(formik.errors.precoAtacado)}
                                    helperText={formik.touched.precoAtacado && formik.errors.precoAtacado}
                                    InputProps={{
                                        inputComponent: NumberFormat,
                                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                    }} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    fullWidth
                                    id="quantidadeMinimaAtacado"
                                    name="quantidadeMinimaAtacado"
                                    label="Qt. Mínima Para Atacado"
                                    variant="filled"
                                    defaultValue={0}
                                    disabled={disableFields}
                                    value={formik.values.quantidadeMinimaAtacado}
                                    onChange={formik.handleChange}
                                    error={formik.touched.quantidadeMinimaAtacado && Boolean(formik.errors.quantidadeMinimaAtacado)}
                                    helperText={formik.touched.quantidadeMinimaAtacado && formik.errors.quantidadeMinimaAtacado}
                                    InputProps={{
                                        inputComponent: NumberFormat,
                                    }} />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    id="unidadeMedida"
                                    name="codigoUnidadeMedida"
                                    label="Unidade de Medida"
                                    variant="filled"
                                    disabled={disableFields}
                                    value={formik.values.codigoUnidadeMedida}
                                    onChange={formik.handleChange}
                                    error={formik.touched.codigoUnidadeMedida && Boolean(formik.errors.codigoUnidadeMedida)}
                                    helperText={formik.touched.codigoUnidadeMedida && formik.errors.codigoUnidadeMedida}>
                                    <MenuItem key={''} value={''} >Selecione a unidade de medida</MenuItem>
                                    {unidadesDeMedida && unidadesDeMedida.map((option) => (
                                        <MenuItem key={option.id} value={option.id} >{option.label}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    id="idCategoria"
                                    name="idCategoria"
                                    label="Categoria"
                                    variant="filled"
                                    disabled={disableFields}
                                    value={formik.values.idCategoria}
                                    onChange={formik.handleChange}
                                    error={formik.touched.idCategoria && Boolean(formik.errors.idCategoria)}
                                    helperText={formik.touched.idCategoria && formik.errors.idCategoria}>
                                    <MenuItem key={''} value={''} >Selecione a categoria</MenuItem>
                                    {categorias && categorias.map((option) => (
                                        <MenuItem key={option.id} value={option.id} >{option.nome}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12}>
                                <Box bgcolor="text.secondary" color="background.paper" p={2}>
                                    <Typography variant='h6' component='p'>
                                        Medidas
                                    </Typography>
                                    Para garantirmos que o valor do frete seja calculado corretamente para o seu cliente, precisamos de atenção extra nos dados abaixo. Informações precisas evitam cobranças inesperadas das transportadoras e garantem uma experiência de compra mais fluida.
                                </Box>
                            </Grid>

                            <Grid item xs={6} sm={3}>
                                <TextField
                                    fullWidth
                                    id="peso"
                                    name="peso"
                                    label="Peso (Kg)"
                                    variant="filled"
                                    defaultValue={0}
                                    disabled={disableFields}
                                    value={formik.values.peso}
                                    onChange={formik.handleChange}
                                    error={formik.touched.peso && Boolean(formik.errors.peso)}
                                    helperText={formik.touched.peso && formik.errors.peso}
                                    InputProps={{
                                        inputComponent: NumberFormat,
                                    }} />
                            </Grid>

                            <Grid item xs={6} sm={3}>
                                <TextField
                                    fullWidth
                                    id="altura"
                                    name="altura"
                                    label="Altura (cm)"
                                    variant="filled"
                                    defaultValue={0}
                                    disabled={disableFields}
                                    value={formik.values.altura}
                                    onChange={formik.handleChange}
                                    error={formik.touched.altura && Boolean(formik.errors.altura)}
                                    helperText={formik.touched.altura && formik.errors.altura}
                                    InputProps={{
                                        inputComponent: NumberFormat,
                                    }} />
                            </Grid>

                            <Grid item xs={6} sm={3}>
                                <TextField
                                    fullWidth
                                    id="largura"
                                    name="largura"
                                    label="Largura (cm)"
                                    variant="filled"
                                    defaultValue={0}
                                    disabled={disableFields}
                                    value={formik.values.largura}
                                    onChange={formik.handleChange}
                                    error={formik.touched.largura && Boolean(formik.errors.largura)}
                                    helperText={formik.touched.largura && formik.errors.largura}
                                    InputProps={{
                                        inputComponent: NumberFormat,
                                    }} />
                            </Grid>

                            <Grid item xs={6} sm={3}>
                                <TextField
                                    fullWidth
                                    id="comprimento"
                                    name="comprimento"
                                    label="Comprimento (cm)"
                                    variant="filled"
                                    defaultValue={0}
                                    disabled={disableFields}
                                    value={formik.values.comprimento}
                                    onChange={formik.handleChange}
                                    error={formik.touched.comprimento && Boolean(formik.errors.comprimento)}
                                    helperText={formik.touched.comprimento && formik.errors.comprimento}
                                    InputProps={{
                                        inputComponent: NumberFormat,
                                    }} />
                            </Grid>

                            <Grid item xs={12}>
                                <Box bgcolor="text.secondary" color="background.paper" p={2}>
                                    <Typography variant='h6' component='p'>
                                        Variações
                                    </Typography>
                                    Se o produto não possuir variações, selecione a opção "Não se aplica" nos campos Tamanho, Cor e Estampa e informe a quantidade total do produto em estoque.
                                </Box>
                            </Grid>

                            {
                                formik.values.variacoes.map((ing, ix) => (
                                    <Box style={{ margin: '10px', width: '100%', backgroundColor: '#f1f1f1', padding: '10px', borderRadius: '5px' }}>
                                        <Grid container spacing={1} direction='row' justifyContent='space-between' alignItems='center' style={{ marginBottom: '10px' }}>
                                            <Typography>Variação #{ix + 1}</Typography>
                                            {
                                                (ix > 1) || (formik.values.variacoes.length > 1)
                                                    ?
                                                    <Button color='secondary' variant='contained' onClick={e => handleDeleteVariacao(ing.id, ix)} disabled={disableFields}>Excluir Variação</Button>
                                                    :
                                                    false
                                            }
                                        </Grid>

                                        <Grid container spacing={2}>
                                            <Grid item xs={6} sm={3}>
                                                <Autocomplete
                                                    key={ix}
                                                    id={`tamanho-${ix}`}
                                                    disabled={disableFields || editando && ing.id > 0}
                                                    options={tamanhoProduto || []}
                                                    getOptionLabel={(option) => option.descricao || ""}
                                                    // Vincula o valor atual do estado ao objeto da lista
                                                    value={tamanhoProduto.find(opt => opt.id === ing.codigoTamanho) || null}

                                                    onChange={(event, newValue) => {
                                                        handleChangeVariacao({
                                                            target: {
                                                                name: 'codigoTamanho',
                                                                value: newValue ? newValue.id : ""
                                                            }
                                                        }, ix);
                                                    }}

                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Tamanho"
                                                            variant="filled"
                                                            fullWidth
                                                            name="codigoTamanho"
                                                            error={
                                                                formik.errors.variacoes &&
                                                                formik.errors.variacoes[ix] &&
                                                                Boolean(formik.errors.variacoes[ix].codigoTamanho)
                                                            }
                                                            helperText={
                                                                formik.errors.variacoes &&
                                                                formik.errors.variacoes[ix] &&
                                                                formik.errors.variacoes[ix].codigoTamanho
                                                            }
                                                        />
                                                    )}

                                                    // Configurações extras para busca
                                                    noOptionsText="Nenhum resultado"
                                                    clearText="Limpar"
                                                    openText="Abrir"
                                                />
                                            </Grid>

                                            <Grid item xs={6} sm={3}>
                                                <Autocomplete
                                                    key={ix}
                                                    id={`cor-${ix}`}
                                                    disabled={disableFields || editando && ing.id > 0}
                                                    options={corProduto || []}
                                                    getOptionLabel={(option) => option.descricao || ""}
                                                    // Vincula o valor atual do estado ao objeto da lista
                                                    value={corProduto.find(opt => opt.id === ing.codigoCor) || null}

                                                    onChange={(event, newValue) => {
                                                        handleChangeVariacao({
                                                            target: {
                                                                name: 'codigoCor',
                                                                value: newValue ? newValue.id : ""
                                                            }
                                                        }, ix);
                                                    }}

                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Cor"
                                                            variant="filled"
                                                            fullWidth
                                                            name="codigoCor"
                                                            error={
                                                                formik.errors.variacoes &&
                                                                formik.errors.variacoes[ix] &&
                                                                Boolean(formik.errors.variacoes[ix].codigoCor)
                                                            }
                                                            helperText={
                                                                formik.errors.variacoes &&
                                                                formik.errors.variacoes[ix] &&
                                                                formik.errors.variacoes[ix].codigoCor
                                                            }
                                                        />
                                                    )}

                                                    // Configurações extras para busca
                                                    noOptionsText="Nenhum resultado"
                                                    clearText="Limpar"
                                                    openText="Abrir"
                                                />


                                            </Grid>
                                            <Grid item xs={6} sm={3}>
                                                <Autocomplete
                                                    key={ix}
                                                    id={`estampa-${ix}`}
                                                    disabled={disableFields || editando && ing.id > 0}
                                                    options={estampaProduto || []}
                                                    getOptionLabel={(option) => option.descricao || ""}
                                                    // Vincula o valor atual do estado ao objeto da lista
                                                    value={estampaProduto.find(opt => opt.id === ing.codigoEstampa) || null}

                                                    onChange={(event, newValue) => {
                                                        handleChangeVariacao({
                                                            target: {
                                                                name: 'codigoEstampa',
                                                                value: newValue ? newValue.id : ""
                                                            }
                                                        }, ix);
                                                    }}

                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Estampa"
                                                            variant="filled"
                                                            fullWidth
                                                            name="codigoEstampa"
                                                            error={
                                                                formik.errors.variacoes &&
                                                                formik.errors.variacoes[ix] &&
                                                                Boolean(formik.errors.variacoes[ix].codigoEstampa)
                                                            }
                                                            helperText={
                                                                formik.errors.variacoes &&
                                                                formik.errors.variacoes[ix] &&
                                                                formik.errors.variacoes[ix].codigoEstampa
                                                            }
                                                        />
                                                    )}

                                                    // Configurações extras para busca
                                                    noOptionsText="Nenhum resultado"
                                                    clearText="Limpar"
                                                    openText="Abrir"
                                                />


                                            </Grid>
                                            <Grid item xs={6} sm={3}>
                                                <TextField
                                                    fullWidth
                                                    id="estoque"
                                                    name="estoque"
                                                    label="Estoque"
                                                    variant="filled"
                                                    disabled={disableFields}
                                                    defaultValue={0}
                                                    value={ing.estoque}
                                                    onChange={e => handleChangeVariacao(e, ix)}
                                                    InputProps={{
                                                        inputComponent: NumberFormat,
                                                    }} />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ))
                            }
                            {typeof formik.errors.variacoes === 'string' && (
                                <Typography color="error" style={{ margin: '8px' }}>
                                    {formik.errors.variacoes}
                                </Typography>
                            )}
                            <Grid container spacing={1} pt={4} style={{ margin: '8px' }}>
                                <Grid item >
                                    <Button color='secondary' variant='contained' onClick={handleAddVariacao} disabled={disableFields}>Adicionar Variação</Button>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={<Switch
                                        checked={formik.values.ativo}
                                        onChange={formik.handleChange}
                                        name="ativo"
                                        disabled={disableFields} />}
                                    label="Publicar Produto?"
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={1} pt={4} style={{ paddingTop: '25px', paddingBottom: '25px' }}>
                            <If test={!disableFields}>
                                <Grid item style={{ paddingRight: '15px' }}>
                                    <Button type='submit' color='primary' className={classes.submitButton} disabled={loading} variant='contained' disabled={disableFields}>Salvar</Button>
                                </Grid>

                                <Grid item style={{ paddingRight: '15px' }}>
                                    <Button variant='contained' href={`/produtos`}>Cancelar</Button>
                                </Grid>
                            </If>
                            <If test={disableFields}>
                                <Grid item style={{ paddingRight: '15px' }}>
                                    <Button variant='contained' href='/produtos'>Voltar</Button>
                                </Grid>
                                <Grid item style={{ paddingRight: '15px' }}>
                                    <Button variant="contained" color='secondary' href={`/produtos/imagens-do-produto/${produto.id}`}>Adicionar imagem do produto</Button>
                                </Grid>
                            </If>
                        </Grid>
                    </div>
                </form>
            </Grid >

            {/* O componente Loading fica ouvindo o estado isProcessing */}
            <LoadingOverlay
                open={isProcessing || processing}
                message={processingMessage}
            />
        </>
    )
}

const mapStateToProps = state => ({
    idDialogShow: state.dialog.idDialogShow,
    categorias: state.categoria.categorias,
    produto: state.produto.produto,
    corProduto: state.produto.corProduto,
    tamanhoProduto: state.produto.tamanhoProduto,
    estampaProduto: state.produto.estampaProduto,
    negocioDoUsuario: state.negocio.negocioDoUsuario,
    loading: state.produto.loading,
    processing: state.produto.processing,
    produtoSalvo: state.produto.produtoSalvo,
})
const mapDispatchToProps = dispatch => bindActionCreators({
    toggleShowDialog,
    getProduto,
    getCategoriasAtivas,
    getCorProduto,
    getTamanhoProduto,
    getEstampaProduto,
    addProduto,
    editProduto,
    uploadImagemProduto,
    deletarVariacaoDoProduto
}, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(ProdutoCadastro)